
  import { Component, Prop, Vue }                 from 'vue-property-decorator';
  import { Action, Getter }                       from 'vuex-class';
  import { EventBus,
           ConfigThemeService,
           ResponsiveUiHelper,
           MessageEntity, 
           AttachmentTypeEnum,
           AttachmentButtonEntity,
           SessionEntity,
           UserMessageTypeEnum, 
           ConfigService,
           MessageOriginEnum,
           BotMessageTypeEnum }                    from '../..';


  @Component({
    name: 'MessageComponent',
  })
  export default class MessageComponent extends Vue {
    @Prop() msg!: MessageEntity;
    @Prop() keyId!: String;

    @Action('sendMessage', { namespace: 'messages' }) sendMessage: any;
    @Action('setSessionFeedbackScoreAvailable', { namespace: 'conversationSession' }) setSessionFeedbackScoreAvailable: any;

    @Getter('isRequestInProgress', { namespace: 'messages' }) isRequestInProgress!: boolean;
    @Getter('feedbackCurrentSessionScore', { namespace: 'conversationSession' }) feedbackCurrentSessionScore!: number;
    @Getter('isLatestRequestSucceed', { namespace: 'messages' }) isLatestRequestSucceed!: boolean;
    @Getter('conversationSessionInfo', { namespace: 'conversationSession' }) conversationSessionInfo!: SessionEntity | undefined;
    
    protected messageRealHeight: number = 0;
    protected messageWantedHeight: number = 0;    
    protected isMessageTooLarge: boolean = false;
    protected isAllMessageDisplayed: boolean = false; 
    protected isFullscreenDisplay: boolean = false;
    protected isBotOptionsListExpanded : boolean = false;

    get elevation() {
      return (this.isTileMessageBoxShapeActive || this.isOutlinedMessageBoxShapeActive) ? 0 : 1;
    }

    get messageId() {
      // The id now depends on the key that is passed in parameter
      return `message-${this.msg.msgId}`;
    }

    get messageTextId() {
      return `${this.messageId}-text`;
    }

    get isBotMessageOrigin() {
      return (this.msg.origin !== MessageOriginEnum.USER)
    }

     get isBotMessageNotification() {
      return (this.msg.type === BotMessageTypeEnum.NOTIFICATION)
    }
    
    get textFontSize() {
      if(ConfigService.getDefaultPanelTextFontSize() !== undefined){
        return { fontSize : ConfigService.getDefaultPanelTextFontSize()+'px !important'};
      } else{ // if user didn't define font size => use breakpoints of ChatView.vue
        return {}; 
      }
    }

    // If the message exceeds the conversation size then we resize it.
    checkMessageWidth() {
      let msgElem: HTMLElement | null = document.getElementById(this.messageId);
      let msgList: HTMLElement | null = document.getElementById("chatbox-msg-list");
      if (msgElem && msgList && msgElem.clientWidth > msgList.clientWidth ) {
        msgElem.style.display = "block";
      }
    }

    /** ******************************************************************************************* */
    /**                                       MESSAGE BOX ITEMS                                     */
    /** ******************************************************************************************* */

    get isTileMessageBoxShapeActive() {

      if(this.isBotMessageNotification){
        return (ConfigThemeService.getNotificationShape()  === 'tile');
      }

      const configShapes: any | undefined = this.isBotMessageOrigin ? ConfigThemeService.getBotMessageBoxShape()
                                                                    : ConfigThemeService.getUserMessageBoxShape();
      return (configShapes && configShapes === 'tile');
    }

    get isRaisedMessageBoxShapeActive() {

      if(this.isBotMessageNotification){
        return (ConfigThemeService.getNotificationShape()  === 'raised');
      }

      const configShapes: any | undefined = this.isBotMessageOrigin ? ConfigThemeService.getBotMessageBoxShape()
                                                                    : ConfigThemeService.getUserMessageBoxShape();
      return (configShapes && configShapes === 'raised');
    }

    get isOutlinedMessageBoxShapeActive() {

      if(this.isBotMessageNotification){
        return (ConfigThemeService.getNotificationShape()  === 'outlined');
      }

      const configShapes: any | undefined = this.isBotMessageOrigin ? ConfigThemeService.getBotMessageBoxShape()
                                                                    : ConfigThemeService.getUserMessageBoxShape();
      return (configShapes && configShapes === 'outlined');
    }

    get messageBoxStyles() {
      if(this.isBotMessageNotification){
        return ConfigThemeService.getBotMessageNotificationStyles(this.$vuetify);
      }

      return this.isBotMessageOrigin  ? ConfigThemeService.getBotMessageBoxStyles(this.$vuetify)
                                      : ConfigThemeService.getUserMessageBoxStyles(this.$vuetify);
    }

    get buttonAttachments() {
      return this.msg.attachments ? this.msg.attachments.get(AttachmentTypeEnum.BUTTON) : [];
    }

    get maxBotOptionsAllowed() {
      return ConfigService.getMaxBotOptionsAllowed();
    }

    onButtonMessageSelected(btn: AttachmentButtonEntity) {
      if (btn.text) {
        btn.alreadyClicked = true;
        this.sendMessage({ sessionInfo: this.conversationSessionInfo, 
                           msgText: btn.text,
                           textContent: btn.key,
                           updateLoadingState: true,
                           waitForAnswer: true, 
                           msgType: UserMessageTypeEnum.USER_SELECTED_SUGGESTION });    
      }
    }

    get getQuoteLinkTextColor() {
      return ConfigThemeService.getQuoteLinkTextColor(this.$vuetify);
    }

    get getQuoteLinkUnderlineColor() {
      return ConfigThemeService.getQuoteLinkUnderlineColor(this.$vuetify);
    }

    get getMessagePrefix() {
      if (this.msg.type === UserMessageTypeEnum.USER_MSG) {
        return ConfigService.getHiddenTextUser();
      }
      else {
        return ConfigService.getHiddenTextBot();
      }
    }

    get getExplainBotMessageText() {
      return ConfigService.getHiddenTextExplainBot();
    }

    /** ******************************************************************************************* */
    /**                                      MESSAGE BOX BUTTONS                                    */
    /** ******************************************************************************************* */
    
    get isOutlinedMessageBoxButtonsShapeActive() {
      if(this.isBotMessageNotification){
        return (ConfigThemeService.getNotificationShape()  === 'outlined');
      }

      return (ConfigThemeService.getMessageBoxButtonsShape() && ConfigThemeService.getMessageBoxButtonsShape() === 'outlined');
    }

    get isTileMessageBoxButtonsShapeActive() {
      if(this.isBotMessageNotification){
        return (ConfigThemeService.getNotificationShape()  === 'tile');
      }

      return (ConfigThemeService.getMessageBoxButtonsShape() && ConfigThemeService.getMessageBoxButtonsShape() === 'tile');
    }

    get isDepressedMessageBoxButtonsShapeActive() {
      if(this.isBotMessageNotification){
        return (ConfigThemeService.getNotificationShape()  === 'depressed');
      }

      return (ConfigThemeService.getMessageBoxButtonsShape() && ConfigThemeService.getMessageBoxButtonsShape() === 'depressed');
    }

    get messageBoxButtonsStyles() {
      if(this.isBotMessageNotification){
        return ConfigThemeService.getBotMessageNotificationButtonsStyles(this.$vuetify);
      }

      return ConfigThemeService.getMessageBoxButtonsStyles(this.$vuetify);
    }

    getMessageBoxButtonsStyles(alreadyClicked: boolean) {
      return this.isRequestInProgress || alreadyClicked ? ConfigThemeService.getMessageBoxButtonsDisabledStyles(this.$vuetify) 
                                                        : ConfigThemeService.getMessageBoxButtonsStyles(this.$vuetify);
    }

    /** ******************************************************************************************* */
    /**                               COMPONENT BEHAVIOR FUNCTIONS                                  */
    /** ******************************************************************************************* */
    
    focusFirstButton() {
      if(this.buttonAttachments && this.buttonAttachments.length > 0){
        const firstButton = this.buttonAttachments[0];
        const button = document.getElementById(firstButton.attId);
        if(button){
          button.focus();
        }
      }
    }

    created() {
      EventBus.$on('ON_RESIZE_SCREEN_MODE', (isFullscreen: boolean) => {
        this.isFullscreenDisplay = isFullscreen;
        this.checkMessageWidth();
      });

      // Adding colors from the config
      const r : HTMLElement = document.querySelector(':root') as HTMLElement
      r.style.setProperty('--quoteLinkColor', this.getQuoteLinkTextColor)
      r.style.setProperty('--quoteLinkTextDecorationColor', this.getQuoteLinkUnderlineColor)

      this.isFullscreenDisplay = ResponsiveUiHelper.isFullscreenDisplay(this.$vuetify);
    }

    mounted() {
      this.focusFirstButton();

      // Make the message content visible to notify the screen reader
      setTimeout(() => {
        this.$el.querySelector('#str-msg-bot-content')?.setAttribute('aria-hidden', 'false');
      }, 100);
    }

    destroyed(){
      EventBus.$off('ON_RESIZE_SCREEN_MODE');
    }

  }
